import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { Sigil } from '../_nav/Sigil';
import { Wordmark } from '../_nav/Wordmark';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';

// 05 МИССИЯ — manifesto screen.
// Texts: README.md §5 "05 МИССИЯ" (byte-equal). Layout: DESIGN.md §10
// (top-left wordmark, sigil watermark via atmosphere) + §3.2 (rubric → Lora
// display → Helvetica body) + §3.4 (.vibe for «вайб/лоу/ноу»).
// Sigil at the end of the body is rendered via the Sigil component — the
// literal `⚽` glyph in source would violate the "no emoji other than sigil
// component" rule (DESIGN.md §11, .cursorrules TOP-10 #10).
// Atmosphere — full (2 spheres + 2 shafts, intensity full) per master prompt
// §7.8 + sub-task brief. Mount uses a soft 4-step rise so the manifesto reads
// in cadence; the universal swipe-down hint anchors the bottom.
//
// Wave 3 (2026-06-04, WAVE_3_BRIEF.md §4.7):
//   - Lora manifesto splits into 4 word-spans on `.motion-word-reveal`
//     (.module-05-word--1..4, 200ms between). Internal <br> inside span 4
//     preserves the original «Эстетика русского / футбола» line-break.
//   - Inline sigil at the end of the last body paragraph gets a one-time
//     `.module-05-sigil-pulse` (scale 0.4 → 1.0 over 800ms, ease-out) —
//     timed to land just after the final body paragraph settles in.
//   - Body «вайб/лоу/ноу» wraps `.vibe.motion-shimmer` (italic + gradient).

export const meta: ModuleMeta = {
  id: '05-mission',
  title: 'МИССИЯ',
  status: 'approved',
  viewport: '390x844',
};

export default function MissionModule() {
  return (
    <section
      className="relative min-h-full w-full bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={2} shafts={2} intensity="full" />

      <Wordmark variant="nav" />

      <div
        className="relative z-10 flex min-h-full flex-col px-sp-6"
        style={{ paddingTop: 'calc(max(var(--safe-top, 0px), 1.25rem) + 56px)' }}
      >
        <div className="flex flex-col gap-sp-7">
          <p className="module-05-rise text-rubric-mono">
            // МИССИЯ
          </p>

          {/* Lora 40px manifesto. Author line-breaks per README §5 ASCII layout —
              matches the same hardcoded-break pattern used on 01-hero subtitle.
              Wave 3: each phrase is its own word-reveal span. Span 4 keeps the
              internal «Эстетика русского / футбола» break so the byte-equal text
              ("«Эстетика русского\nфутбола».") and visual cadence both survive. */}
          <h2 className="font-display text-display leading-[1.1] tracking-[-0.02em] text-text-primary">
            <span className="motion-word-reveal module-05-word--1">Наша команда</span>
            <br />
            <span className="motion-word-reveal module-05-word--2">продолжает миссию</span>
            <br />
            <span className="motion-word-reveal module-05-word--3">экосистемы проектов</span>
            <br />
            <span className="motion-word-reveal module-05-word--4">
              <span className="vibe motion-shimmer">
                «Эстетика русского
                <br />
                футбола»
              </span>
            </span>
          </h2>

          <div className="flex flex-col gap-sp-5 pb-sp-9">
            <p className="module-05-rise module-05-rise--d2 font-ui text-body leading-[1.5] text-text-primary">
              Мы видим, как сильно меняется отечественный спорт, и понимаем, как
              стать проводником в мире этих изменений — через цифровизацию
              ценностей и идей, которые навсегда останутся с любителями спорта.
            </p>
            <p className="module-05-rise module-05-rise--d3 font-ui text-body leading-[1.5] text-text-primary">
              Русский футбол заслуживает крутого диджитала. Мы делаем это
              возможным благодаря технологиям{' '}
              <span className="vibe motion-shimmer">вайб/лоу/ноу</span> кодинга{' '}
              <span className="module-05-sigil-inline module-05-sigil-pulse">
                <Sigil variant="hud" className="!w-[34px] !opacity-100" />
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Universal swipe-down affordance (matches 01-hero / 02-pain). */}
      <div
        className="hint-arrow-pulse pointer-events-none absolute bottom-sp-7 left-1/2 z-10 flex -translate-x-1/2 items-center justify-center gap-sp-3 text-text-muted"
        aria-hidden
      >
        <span className="inline-block h-[14px] w-[14px] rounded-full border border-current" />
        <span className="text-[1.25rem] leading-none">↓</span>
      </div>
    </section>
  );
}
